/* eslint-disable @typescript-eslint/restrict-plus-operands */
import React from 'react';
import { Bar } from 'react-chartjs-2';
import type { Plugin } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

interface Loja {
  id: number;
  name: string;
  store_name: string | null;
  RatingCompany: Array<{ ratingNumber: number }>;
  totalRatings: number;
  avgRating: number;
  positivePercentage: number;
}

interface LojaChartProps {
  data?: { lojas_mais_bem_avaliadas?: Loja[] };
  style?: React.CSSProperties; // ✅ novo
}

export const LojasChart: React.FC<LojaChartProps> = ({ data, style }) => {
  const lojas = data?.lojas_mais_bem_avaliadas ?? [];
  const isLoading = !data?.lojas_mais_bem_avaliadas;

  const plugins: Array<Plugin<'bar'>> = [ChartDataLabels as Plugin<'bar'>];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getColor = (value: number) => {
    if (value >= 80) return '#2A2C85';
    if (value >= 60) return '#2B2AA9';
    if (value >= 40) return '#3431D1';
    if (value >= 20) return '#5C68F8';
    return '#C4D3FF';
  };

  const chartData = {
    labels: lojas.map((l) => l.store_name ?? l.name),
    datasets: [
      {
        label: 'Porcentagem positiva',
        data: lojas.map((l) => l.positivePercentage),
        backgroundColor: lojas.map((l) => getColor(l.positivePercentage)),
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
      datalabels: {
        anchor: 'end' as const,
        align: 'end' as const,
        formatter: (value: number) => {
          if (value === 0) return null;
          return `${value}%`;
        },
        font: {
          weight: 'bold' as const,
          size: 12,
        },
        color: '#2A2C85',
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        min: 0,
        max: 120,
        ticks: {
          stepSize: 1,
          callback(tickValue) {
            return tickValue.toString();
          },
          display: false,
        },
        grid: {
          drawTicks: false,
          drawOnChartArea: false,
        },
      },
      x: {
        type: 'category' as const,
        ticks: {
          callback(this: any, val, index) {
            const label = this.getLabelForValue(index);
            return label.length > 12 ? label.slice(0, 12) + '...' : label;
          },
        },
        grid: {
          drawTicks: false,
          drawOnChartArea: false,
        },
      },
    },
  };

  const defaultStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1rem',
    maxWidth: '685px',
    minWidth: 304,
    width: '100%',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  };

  return (
    <div style={{ ...defaultStyle, ...style }}>
      {/* Header */}
      <h3 style={{ margin: 0, fontSize: 16, color: '#5A5A5A', marginBottom: '1rem' }}>
        Top 5 lojas
      </h3>

      {/* Chart */}
      {isLoading ? (
        <div
          style={{
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
          }}
        >
          ...
        </div>
      ) : (
        <Bar data={chartData} options={options} plugins={plugins} />
      )}
    </div>
  );
};
